"use client";

import React from "react";

import { formatCurrency } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

export type GiftCardTileSize = "default" | "compact";
export type GiftCardTileTone = "primary" | "muted";

export interface GiftCardTileProps {
  name: string;
  description?: string | null;
  /** Optional artwork above the card details, retaining the gift-card surface. */
  artwork?: React.ReactNode;
  /** Numeric cents are formatted as currency; composed content is rendered as-is. */
  price: React.ReactNode;
  /** Optional supporting content below the price. */
  children?: React.ReactNode;
  eyebrow?: string;
  size?: GiftCardTileSize;
  tone?: GiftCardTileTone;
  /** When provided, the whole card becomes a button. */
  onClick?: () => void;
  /** Slot rendered in the top-right of the chip row (e.g. a dropdown trigger). */
  actions?: React.ReactNode;
  /** Slot rendered in the bottom-right (e.g. a "Custom" badge). */
  badge?: React.ReactNode;
  /** Optional icon shown in the top-right (e.g. a + for custom amounts). */
  icon?: React.ReactNode;
  /** Optional line-clamp override for the description. */
  descriptionClassName?: string;
  ariaLabel?: string;
  "aria-haspopup"?: React.AriaAttributes["aria-haspopup"];
  className?: string;
}

const SIZE_STYLES: Record<
  GiftCardTileSize,
  {
    padding: string;
    eyebrow: string;
    hero: string;
    name: string;
    description: string;
  }
> = {
  default: {
    padding: "p-5",
    eyebrow: "text-tiny tracking-[0.28em]",
    hero: "text-[2.75rem]",
    name: "text-body",
    description: "text-caption",
  },
  compact: {
    padding: "p-4",
    eyebrow: "text-micro tracking-[0.24em]",
    hero: "text-[2rem]",
    name: "text-caption",
    description: "text-tiny",
  },
};

export const GuillocheOrnament: React.FC<{ tone: GiftCardTileTone }> = ({
  tone,
}) => (
  <svg
    aria-hidden
    viewBox="0 0 200 200"
    className={cx(
      "pointer-events-none absolute -bottom-28 -right-24 h-72 w-72",
      tone === "primary" ? "opacity-[0.09]" : "opacity-[0.18]",
    )}
  >
    <g
      stroke="currentColor"
      strokeWidth="0.4"
      fill="none"
      className={
        tone === "primary" ? "text-primary-foreground" : "text-primary"
      }
    >
      {Array.from({ length: 36 }).map((_, i) => {
        const angle = (i * 10 * Math.PI) / 180;
        return (
          <line
            key={i}
            x1="100"
            y1="100"
            x2={100 + Math.cos(angle) * 200}
            y2={100 + Math.sin(angle) * 200}
          />
        );
      })}
      <circle cx="100" cy="100" r="42" />
      <circle cx="100" cy="100" r="64" />
      <circle cx="100" cy="100" r="86" />
    </g>
  </svg>
);

/**
 * Small satin bow — the gift cue, tucked into the top-left corner. Two rounded
 * loops + knot + short trailing tails, sized to sit in for the old card chip.
 */
export const GiftBow: React.FC<{
  tone?: GiftCardTileTone;
  size?: GiftCardTileSize;
  className?: string;
}> = ({ tone = "primary", size = "default", className }) => {
  const isPrimary = tone === "primary";
  return (
    <svg
      aria-hidden
      viewBox="0 0 48 40"
      className={cx(
        size === "compact" ? "h-7 w-[2.1rem]" : "h-8 w-[2.4rem]",
        isPrimary ? "text-primary-foreground" : "text-primary",
        isPrimary
          ? "drop-shadow-[0_1px_1.5px_rgb(0_0_0_/_0.3)]"
          : "drop-shadow-[0_1px_1.5px_rgb(0_0_0_/_0.12)]",
        className,
      )}
    >
      <g fill="currentColor">
        {/* Two ribbon tails trailing down from the knot. */}
        <path
          opacity="0.78"
          d="M21 21 C 18 27 14 31 11 37 L 17 37 C 20 31 22 26 24 21 Z"
        />
        <path
          opacity="0.78"
          d="M27 21 C 30 27 34 31 37 37 L 31 37 C 28 31 26 26 24 21 Z"
        />
        {/* Two rounded loops fanning from the center knot. */}
        <path opacity="0.92" d="M24 18 C 14 6 2 9 4 18 C 2 27 16 27 24 18 Z" />
        <path
          opacity="0.92"
          d="M24 18 C 34 6 46 9 44 18 C 46 27 32 27 24 18 Z"
        />
        <rect x="20" y="12" width="8" height="12" rx="3" />
      </g>
    </svg>
  );
};

/**
 * Top-left "wrapped gift" corner: a diagonal satin ribbon across the corner
 * with the bow tied on it at an angle, so the card reads as gift-wrapped. The
 * ribbon runs off both edges and is clipped by the card's rounded corner.
 */
export const GiftCorner: React.FC<{
  tone?: GiftCardTileTone;
  size?: GiftCardTileSize;
}> = ({ tone = "primary", size = "default" }) => {
  const isPrimary = tone === "primary";
  const compact = size === "compact";
  const band = isPrimary
    ? "from-white/10 via-white/40 to-white/10"
    : "from-primary/12 via-primary/40 to-primary/12";
  const thread = isPrimary ? "bg-white/55" : "bg-primary/55";
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div
        className={cx(
          "absolute -rotate-45 bg-gradient-to-b shadow-[0_1px_2px_rgb(0_0_0_/_0.15)]",
          compact
            ? "-left-[49px] top-[15px] h-[22px] w-[150px]"
            : "-left-[60px] top-[17px] h-[26px] w-[180px]",
          band,
        )}
      >
        <div
          className={cx(
            "absolute inset-x-0 top-1/2 h-px -translate-y-1/2",
            thread,
          )}
        />
      </div>
      <div
        className={cx(
          "absolute -rotate-45",
          compact ? "left-[9px] top-[12px]" : "left-[11px] top-[14px]",
        )}
      >
        <GiftBow tone={tone} size={size} />
      </div>
    </div>
  );
};

export const GiftCardTile: React.FC<GiftCardTileProps> = ({
  name,
  description,
  artwork,
  price,
  children,
  eyebrow = "Digital Gift Card",
  size = "default",
  tone = "primary",
  onClick,
  actions,
  badge,
  icon,
  descriptionClassName,
  ariaLabel,
  "aria-haspopup": ariaHasPopup,
  className,
}) => {
  const sizing = SIZE_STYLES[size];
  const priceLabel = typeof price === "number" ? formatCurrency(price) : price;
  const interactive = Boolean(onClick);
  const isPrimary = tone === "primary";

  const Root = interactive ? "button" : "div";
  const rootProps = interactive
    ? {
        type: "button" as const,
        onClick,
        "aria-haspopup": ariaHasPopup,
        "aria-label":
          ariaLabel ??
          (typeof price === "number"
            ? `Select ${name} for ${priceLabel}`
            : `Select ${name}`),
      }
    : {};

  return (
    <Root
      {...rootProps}
      className={cx(
        "group relative w-full overflow-hidden rounded-2xl text-left",
        artwork ? "flex flex-col" : "aspect-[1.586]",
        "shadow-[0_2px_8px_-2px_rgb(0_0_0_/_0.15),0_10px_24px_-12px_rgb(0_0_0_/_0.25)] ring-1 ring-black/5",
        "transition-[transform,box-shadow] duration-500 ease-out",
        "hover:-translate-y-0.5 hover:shadow-[0_8px_28px_-4px_rgb(0_0_0_/_0.25),0_18px_48px_-12px_rgb(0_0_0_/_0.35)]",
        interactive &&
          "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
        isPrimary
          ? "bg-primary text-primary-foreground"
          : "border border-dashed border-primary/40 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent text-primary",
        className,
      )}
    >
      {isPrimary && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_0%,rgb(255_255_255_/_0.22),transparent_55%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/15 via-transparent to-transparent"
          />
        </>
      )}

      <GuillocheOrnament tone={tone} />

      <div
        aria-hidden
        className={cx(
          "pointer-events-none absolute inset-3 rounded-xl border",
          isPrimary ? "border-white/15" : "border-primary/15",
        )}
      />
      <div
        aria-hidden
        className={cx(
          "pointer-events-none absolute inset-[14px] rounded-[10px] border border-dashed",
          isPrimary ? "border-white/10" : "border-primary/15",
        )}
      />

      {!artwork && <GiftCorner tone={tone} size={size} />}

      {isPrimary && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/2 -translate-x-full -skew-x-12 bg-gradient-to-r from-transparent via-white/25 to-transparent transition-transform duration-[900ms] ease-out group-hover:translate-x-[260%] motion-reduce:hidden"
        />
      )}

      {artwork && (
        <div className="relative">
          {artwork}
          <GiftCorner tone={tone} size={size} />
        </div>
      )}

      <div
        className={cx(
          "relative flex flex-col",
          artwork ? "min-h-[220px] flex-1" : "h-full",
          sizing.padding,
        )}
      >
        <div className="flex items-start justify-end">
          <div className="flex items-center gap-1.5">
            {icon && (
              <div
                className={cx(
                  "rounded-full p-1.5 backdrop-blur-md",
                  isPrimary
                    ? "bg-white/15 text-primary-foreground/90"
                    : "bg-primary/10 text-primary",
                )}
              >
                {icon}
              </div>
            )}
            {actions}
          </div>
        </div>

        <div className="my-auto">
          <p
            className={cx(
              "font-semibold uppercase",
              sizing.eyebrow,
              isPrimary ? "text-primary-foreground/65" : "text-primary/70",
            )}
          >
            {eyebrow}
          </p>
          <span
            className={cx(
              "mt-1 block font-serif font-medium leading-none tracking-tight",
              sizing.hero,
              isPrimary
                ? "tabular-nums text-primary-foreground drop-shadow-[0_1px_0_rgb(0_0_0_/_0.18)]"
                : "text-primary",
            )}
          >
            {priceLabel}
          </span>
          {children}
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <h4
              className={cx(
                "line-clamp-1 font-medium tracking-wide",
                sizing.name,
                isPrimary ? "text-primary-foreground/90" : "text-primary",
              )}
              title={name}
            >
              {name}
            </h4>
            {description && (
              <p
                className={cx(
                  "mt-0.5 line-clamp-1",
                  sizing.description,
                  isPrimary ? "text-primary-foreground/65" : "text-primary/70",
                  descriptionClassName,
                )}
                title={description}
              >
                {description}
              </p>
            )}
          </div>
          {badge && <div className="shrink-0">{badge}</div>}
        </div>
      </div>
    </Root>
  );
};

export const GiftCardTileSkeleton: React.FC<{ className?: string }> = ({
  className,
}) => (
  <div
    className={cx(
      "relative aspect-[1.586] w-full animate-pulse overflow-hidden rounded-2xl bg-muted",
      className,
    )}
  >
    <div className="pointer-events-none absolute inset-3 rounded-xl border border-foreground/5" />
    <div className="pointer-events-none absolute inset-[14px] rounded-[10px] border border-dashed border-foreground/5" />
  </div>
);

export default GiftCardTile;
